import React, { useCallback, useEffect, useState } from 'react';
import { transformData } from '../../super-admin/utils/transformData';
import { useQuery } from '@tanstack/react-query';
import {
  deleteInvoicePayment,
  getAllPaymentHistoryForDeals,
  updatePaymentStatus,
  updateInvoicePayment
} from '../services/api/implementation/impl';
import Modal from '../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import { UpdateStatus } from '../components/forms/UpdateStatus';
import Loader from '../../shared/components/ui/Loader';
import { useMutation } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { Pencil, Trash } from 'lucide-react';
import { PaymentForm } from '../components/forms/PaymentForm';
import EditpaymentForm from '../components/forms/EditpaymetForm';

const SalesPayment = ({ dealID, chequeData }) => {
  const userId = Cookies.get('userId');

  // Track component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Payment Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Sales Payment Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, []);
  // State Variables
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentID, setPaymentID] = useState();
  const [openEditPayment, setOpenEditPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const {
    data: paymentHistory,
    refetch: refetchPaymentHistory,
    isLoading,
    error
  } = useQuery({
    queryKey: ['paymentHistoryInvoive', dealID],
    queryFn: () => getAllPaymentHistoryForDeals(dealID),
    keepPreviousData: true,
    enabled: !!dealID,
    onSuccess: (data) => {
      mixpanel.track('Payment History API Success', {
        user_id: userId,
        total_payments: data?.meta?.total || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Payment History API Failed', {
        user_id: userId,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  useEffect(() => {
    refetchPaymentHistory();
  }, [dealID]);

  const UpdateStatusOptions = [{ id: 'Payment Returned', name: 'Payment Returned' }];

  const ConsolidatedDataFormat = [
    'id',
    'transactionID',
    'mode',
    'amount_paid',
    'created_at',
    'remarks',
    'type'
  ];

  const handleStatusUpdateFn = async (data) => {
    const payload = { status: data.status };
    setIsSubmitting(true);
    try {
      const res = await updatePaymentStatus(paymentID, payload);
      toast.success('Status Updated Successfully');
      refetchPaymentHistory();
      setOpenUpdateStatus(false);
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      toast.error(error?.response?.data?.message || error?.message || 'Error status updating');
    }
  };

  const handleStatusUpdate = (id) => {
    setPaymentID(id);
    setOpenUpdateStatus(true);
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setOpenEditPayment(true);
  };

  const handleDeletePayment = (payment) => {
    setPaymentToDelete(payment);
    setOpenDeleteConfirm(true);
  };

  const confirmDeletePayment = () => {
    if (paymentToDelete) {
      handleDeleteFn(paymentToDelete.id);
      setOpenDeleteConfirm(false);
      setPaymentToDelete(null);
    }
  };

  // Transform data to fit table format
  const ConsolidatedData =
    paymentHistory?.data?.map((item) => transformData(item, ConsolidatedDataFormat)) || [];

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (id) => deleteInvoicePayment(id),
    onMutate: (id) => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: (result, variables) => {
      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        setIsSubmitting(false);
        console.error('Error deleting payment', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error deleting payment');

        return;
      }

      // Handle as success
      setIsSubmitting(false);
      toast.success('Payment Deleted Successfully');

      refetchPaymentHistory();
    },
    onError: (err, id) => {
      setIsSubmitting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error deleting payment');

      mixpanel.track('Delete Payment Failed', {
        user_id: userId,
        payment_id: id,
        error_message: err?.response?.data?.message || err?.message,
        timestamp: new Date().toISOString()
      });
    }
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading data</div>;
  }

  return (
    <>
      <Loader isVisible={isSubmitting} />

      {openUpdateStatus && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Update Status"
          onClose={() => setOpenUpdateStatus(false)}>
          <UpdateStatus
            onClose={() => setOpenUpdateStatus(false)}
            onSubmit={handleStatusUpdateFn}
            statuses={UpdateStatusOptions}
          />
        </Modal>
      )}

      {openEditPayment && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={45}
          name="Edit Payment"
          onClose={() => {
            setOpenEditPayment(false);
            setSelectedPayment(null);
          }}>
          <EditpaymentForm
            onClose={() => {
              setOpenEditPayment(false);
              setSelectedPayment(null);
            }}
            onSuccess={() => {
              refetchPaymentHistory();
              setOpenEditPayment(false);
              setSelectedPayment(null);
            }}
            chequeData={chequeData}
            selectedPayment={selectedPayment}
          />
        </Modal>
      )}

      {openDeleteConfirm && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Delete Payment"
          onClose={() => {
            setOpenDeleteConfirm(false);
            setPaymentToDelete(null);
          }}>
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 mb-6">
              <svg
                className="h-8 w-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>

            {/* Title and Message */}
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Delete Payment</h3>
            <p className="text-gray-600 mb-8 max-w-sm mx-auto">
              Are you sure you want to delete this payment? This action cannot be undone and will
              permanently remove the payment record.
            </p>
            {paymentToDelete && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Transaction ID:</span>{' '}
                  {paymentToDelete.transactionID || '---'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Amount:</span>{' '}
                  {paymentToDelete.amount_paid || '---'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Mode:</span> {paymentToDelete.mode || '---'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Date:</span> {paymentToDelete.created_at || '---'}
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setOpenDeleteConfirm(false);
                  setPaymentToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500">
                Cancel
              </button>
              <button
                onClick={confirmDeletePayment}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Deleting...' : 'Delete Payment'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      <div className="w-[100%] flex flex-col z-5 bg-gray-100">
        <div className="overflow-scroll hide-scrollbar w-full relative rounded-t-lg">
          <table className="text-left w-full min-w-[800px]">
            <thead
              className="bg-gray-200 !text-gray-600 w-full"
              style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
              <tr className="font-normal h-[3rem] w-full whitespace-nowrap">
                <th className="px-4 py-2 border-b font-normal">Transaction Id</th>
                <th className="px-4 py-2 border-b font-normal">Mode</th>
                <th className="px-4 py-2 border-b font-normal">Amount Paid</th>
                <th className="px-4 py-2 border-b font-normal">Date</th>
                <th className="px-4 py-2 border-b font-normal">Remarks</th>
                <th className="px-4 py-2 border-b font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ConsolidatedData && ConsolidatedData?.length > 0 ? (
                ConsolidatedData?.map((payment, index) => {
                  return (
                    <React.Fragment key={payment?.id || index}>
                      <tr className="border-b hover:bg-gray-50 bg-white h-[3.5rem]">
                        <td className="px-4 py-2 text-sm">
                          {payment?.transactionID || payment?.transaction_id || '---'}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className="capitalize">
                            {payment?.mode || payment?.payment_mode || '---'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <div className="flex items-center gap-2">
                            {payment?.type === 'Debited' ? (
                              <svg
                                className="w-4 h-4 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                                />
                              </svg>
                            ) : payment?.type === 'Credited' ? (
                              <svg
                                className="w-4 h-4 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                />
                              </svg>
                            ) : null}
                            {payment?.amount_paid || 0}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {payment?.created_at ? payment.created_at : '---'}
                        </td>
                        <td className="px-4 py-2 text-sm">{payment?.remarks || '---'}</td>
                        <td className="px-4 py-2 text-sm">
                          <div className="flex items-center space-x-2">
                            <Pencil
                              className="w-4 h-4 hover:text-blue-400 cursor-pointer"
                              onClick={() => handleEditPayment(payment)}
                            />
                            <Trash
                              className="w-4 h-4 hover:text-red-400 cursor-pointer"
                              onClick={() => handleDeletePayment(payment)}
                            />
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-10 align-middle">
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <h2 className="text-lg font-bold text-gray-800 mb-1">
                        No Payment Data Found
                      </h2>
                      <p className="text-gray-600 text-sm">
                        No payment history available for this deal.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default SalesPayment;
